<template>
  <div
    ref="inviewRef"
    :aria-disabled="isSoldout"
    :class="[
      'relative bg-surface-light border border-surface-grey rounded-md transition duration-500 delay-400 has-[.calendar-open]:z-20',
      inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
    ]"
  >
    <div class="relative p-4 md:p-6 space-y-4">
      <div class="flex flex-col md:flex-row justify-between items-start">
        <div :class="['mb-4', isSoldout ? 'opacity-50 pr-20 md:pr-0' : '']">
          <div
            v-if="ticket.section_name"
            class="text-primary text-sm mb-1 font-semibold"
          >
            {{ ticket.section_name }}
          </div>

          <div class="h4 mb-1">
            {{ getLocalizedTicketValue(ticket, $i18n.locale, 'name1') }}
          </div>
          <div v-if="ticket.name2" class="text-text text-sm">
            {{ getLocalizedTicketValue(ticket, $i18n.locale, 'name2') }}
          </div>
        </div>

        <div v-if="ticket.type || isSoldout" class="flex flex-wrap gap-2">
          <span v-if="ticket.type" :class="['inline-block text-xs px-2 py-1 font-semibold rounded-sm border-1 border-surface-grey bg-surface-light text-text', isSoldout ? 'opacity-50' : '']">
            {{ ticket.type }}
          </span>
          <span v-if="isSoldout" class="absolute top-4 right-4 md:static md:top-auto md:right-auto text-headings text-xs px-2 py-1 rounded font-semibold badge-soldout">
            {{ $t('common.soldout') }}
          </span>
        </div>
      </div>

      <div :class="['flex justify-between items-center', isSoldout ? 'opacity-50' : '']">
        <div>
          <div class="flex items-baseline space-x-2">
            <!-- Only sent by the server when the logged in sso user has a price for this ticket -->
            <div v-if="memberPrice !== null" class="heavy wrap-anywhere">
              {{ ssoPriceLabel }} {{ formatPrice(memberPrice, event.currency, $i18n.locale) }}
            </div>

            <!-- With a member price the regular price comes second in the "price before" style (grey, struck through) -->
            <div :class="memberPrice !== null ? 'text-text line-through text-sm' : 'heavy'">
              <span v-if="showFromPrefix" class="text-sm font-normal mr-1">{{ $t('common.from') }}</span>{{ formatPrice(displayPrice, event.currency, $i18n.locale) }}
            </div>

            <!--div
              v-if="ticket.beforePrice"
              class="text-text line-through text-sm"
            >
              {{ $t('common.before') }}
              {{
                formatPrice(ticket.beforePrice, event.currency, $i18n.locale)
              }}
            </div -->
          </div>
          <div class="text-text text-sm" v-if="actualTicketFee && actualTicketFee > 0">
            + {{ formatPrice(actualTicketFee, event.currency, $i18n.locale) }}
            {{ $t('common.fee') }}
          </div>
        </div>

        <InputCount
            class="w-34"
            :model-value="cart.currentCount[ticket.id]"
            @update:model-value="onCountChange"
            :step="ticket.buy_amount_increment"
            :max="ticket.maxtickets"
            :first-step="ticket.mintickets"
            :disabled="isSoldout"
            required
          />
      </div>
    </div>

    <BaseSlideTransition>
      <div v-if="hasTicketCustomFields && cart.currentCount[ticket.id] > 0">
        <div
          class="py-4 px-6 border-t border-surface-grey bg-surface-grey-light font-semibold break-words"
        >
          {{ $t('checkout.add_info_to', { name: getLocalizedTicketValue(ticket, $i18n.locale, 'name1') }) }}
        </div>

        <CheckoutCustomFields
          type="ticket"
          :ticket-id="ticket.id"
          :event-id="eventID"
          :filters="['per_ticket','per_tickettype']"
          :errors="undefined"
        />
      </div>
    </BaseSlideTransition>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import { useCartStore } from '@/stores/cart';
  import type { EventCheckoutData, TicketCheckoutData } from '@/types';
  import { formatPrice } from '@/utils/formatPrice';
  import { getLocalizedTicketValue } from '@/utils/ticketItemUtil';
  import { useInView } from '@/utils/useInView';
  import { useSsoLabels } from '@/utils/useSsoLabels';

  import BaseSlideTransition from '../ui/BaseSlideTransition.vue';
  import InputCount from '../ui/inputs/InputCount.vue';
  import CheckoutCustomFields from './CheckoutCustomFields.vue';

  const props = defineProps<{
    ticket: TicketCheckoutData;
    event: EventCheckoutData;
    eventID: number | string | undefined;
  }>();

  const { ssoPriceLabel } = useSsoLabels();

  //const customField = ref<CustomField[]>([]); // temp value, was ticket.customFields above!
  //const tags = ref(['VIP', 'Backstage']); // temp value, was ticket.tags above!

  const cart = useCartStore();

  const cartEvent = cart.items.find(
    item => item.id == props.eventID,
  );

  const ticketCount = Number(cartEvent?.tickets.find(t => t.id == props.ticket.id)?.count) || 0;

  cart.currentCount[props.ticket.id] = ticketCount;

  function onCountChange(newCount: number) {
    cart.currentCount[props.ticket.id] = newCount;
    cart.queueTicketChange(props.eventID, props.ticket.id, newCount);
  }

  const hasTicketCustomFields = computed(() => {
    if (!cart.currentOrderData.customfields_data || !props.eventID) return false;
    const eventFields = cart.currentOrderData.customfields_data[props.eventID];
    if (!eventFields) return false;
    return Object.values(eventFields).some(
      field => field.ticketID == props.ticket.id && ['per_ticket', 'per_tickettype'].includes(field.type),
    );
  });

  const hasPriceGroups = computed(() => (props.ticket.price_groups?.length ?? 0) > 0);
  const confirmedTotal = computed(() => cart.confirmedSeatPrices[props.ticket.id] ?? null);
  const ticketCountInCart = computed(() => cart.currentCount[props.ticket.id] || 1);
  const displayPrice = computed(() => {
    if (hasPriceGroups.value) {
      if (confirmedTotal.value != null && ticketCountInCart.value > 0) {
        return Math.round(confirmedTotal.value / ticketCountInCart.value);
      }
      return props.ticket.min_price;
    }
    return props.ticket.price;
  });
  const showFromPrefix = computed(() => hasPriceGroups.value && confirmedTotal.value == null);
  // The member price of a logged in sso user. Not used for tickets with price groups (seat prices).
  const memberPrice = computed(() => (hasPriceGroups.value ? null : props.ticket.memberprice ?? null));

  const actualTicketFee = computed(() => {
    // The fee follows the price the user actually pays
    const price = memberPrice.value ?? displayPrice.value;
    return props.ticket.ticketfee + price * props.ticket.ticketfee_extra;
  });

  const isSoldout = computed(() => props.ticket.available == 0);

  const { inviewRef, inView } = useInView();
</script>
